import axios, { type AxiosProgressEvent } from 'axios';

import { type Common } from '../common.js';
import {
  type Command,
  type CreateValues,
  type DocumentTemplate,
  type FindAllOptions,
  type UpdateValues,
} from '../models/document/template.js';
import { type PaginatedQueryOptions } from '../models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from '../models/pagination/PaginationResponse.type.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/document-templates';
  }

  async findAll(options: FindAllOptions = {}): Promise<DocumentTemplate[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<DocumentTemplate>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<DocumentTemplate> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  downloadURL(id: string): string {
    return this.common.constructPath(`${this.baseEndpoint}/${id}/file`);
  }

  async create(values: CreateValues): Promise<DocumentTemplate> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<DocumentTemplate> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async remove(id: string) {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async getUploadURL(fileName: string): Promise<{ url: string; gsPath: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/upload-url`, {
      fileName,
    });
    return await this.common.getJSON(url);
  }

  async upload(args: {
    file: File;
    url: string;
    progressHandler?: (event: AxiosProgressEvent) => void;
  }): Promise<number> {
    const { file, url, progressHandler } = args;
    const res = await axios.put(url, file, {
      onUploadProgress: progressHandler,
      headers: { 'Content-Type': 'application/pdf' },
    });
    return res.status;
  }

  async preview(body: { gsPath: string; commands: Command[] }): Promise<Blob> {
    const url = this.common.constructPath(`${this.baseEndpoint}/preview`);
    const response = await this.common.authedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (response.status === 200) {
      return await response.blob();
    }
    if (response.headers.get('Content-Type') === 'application/json') {
      const json = await response.json();
      throw new Error(json.message);
    }
    const text = await response.text();
    throw new Error(text);
  }

  async linkFormTemplate(args: { documentTemplateId: string; previsitFormTemplateId: string }) {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${args.documentTemplateId}/form-template`
    );
    await this.common.postJSON(url, {
      previsitFormTemplateId: args.previsitFormTemplateId,
    });
  }
}
